import React, { MouseEvent, TouchEvent, useState } from 'react';

import SlideButton from 'components/ui/Button/SlideButton';

import SliderItem, { SliderItemProps } from './SliderItem';

import css from './Slider.module.scss';

interface Props extends React.PropsWithChildren {
    totalItems: number;
    transformValue?: number;
    buttonsOffsetTop?: string;
}

const Slider: React.FC<Props> & { Item: React.FC<SliderItemProps> } = ({
    children,
    totalItems,
    buttonsOffsetTop,
    transformValue = 100,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    // Required minimum interval between touchStart and touchEnd to be detected as a swipe
    const minSwipeDistance = 25;

    const onTouchStart = (e: TouchEvent | MouseEvent) => {
        setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
        if ('touches' in e) {
            setTouchStart(e.touches[0].clientX);
        } else {
            setTouchStart(e.clientX);
        }
    };

    const onTouchMove = (e: TouchEvent | MouseEvent) => {
        if ('touches' in e) {
            setTouchEnd(e.touches[0].clientX);
        } else {
            setTouchEnd(e.clientX);
        }
    };

    const onTouchEnd = () => {
        if (touchStart && touchEnd) {
            const distance = touchStart - touchEnd;
            const isLeftSwipe = distance > minSwipeDistance;
            const isRightSwipe = distance < -minSwipeDistance;
            if (isLeftSwipe) updateIndex(currentIndex + 1);
            if (isRightSwipe) updateIndex(currentIndex - 1);
        }
    };

    const updateIndex = (index: number) => {
        if (index < 0) {
            index = 0;
        } else if (index >= totalItems) {
            index = totalItems - 1;
        }

        setCurrentIndex(index);
    };

    return (
        <div className={css.container}>
            <div
                className={css.inner}
                style={{ transform: `translateX(-${currentIndex * transformValue}%)` }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onMouseDown={onTouchStart}
                onMouseMove={onTouchMove}
                onMouseUp={onTouchEnd}
            >
                {children}
            </div>
            <div className={css.indicators}>
                {currentIndex !== 0 && (
                    <SlideButton
                        position="left"
                        onClickEvent={() => updateIndex(currentIndex - 1)}
                        offsetTop={buttonsOffsetTop}
                    />
                )}
                {currentIndex !== totalItems - 1 && (
                    <SlideButton
                        position="right"
                        onClickEvent={() => updateIndex(currentIndex + 1)}
                        offsetTop={buttonsOffsetTop}
                    />
                )}
            </div>
        </div>
    );
};

Slider.Item = SliderItem;

export default Slider as typeof Slider & { Item: typeof SliderItem };
